import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { apiRequest } from "../config/api";
import { Player } from "../types";

type MergeResolutionChoice = "keep_target" | "keep_source" | "skip";

type MergeResolution = {
  type: string;
  key: string;
  action: MergeResolutionChoice;
};

type MergeConflict = {
  type: string;
  key: string;
  description: string;
  sourceDetail?: any;
  targetDetail?: any;
};

type MergePreview = {
  source: {
    id: string;
    name: string;
    division?: string;
    hasAccount: boolean;
    mergeInProgress: boolean;
  };
  target: {
    id: string;
    name: string;
    division?: string;
    hasAccount: boolean;
    mergeInProgress: boolean;
  };
  additions: {
    leagueDays: Array<{ id: string; name?: string }>;
    tournaments: Array<{ id: string; name?: string }>;
    bets: Array<{ id: string; leagueDayId: string }>;
    simpleCounts: Record<string, number>;
  };
  conflicts: MergeConflict[];
};

interface MergePlayersDialogProps {
  open: boolean;
  targetPlayer: Player | null;
  onClose: () => void;
  onMerged: () => void;
}

const resolutionLabel = (choice: MergeResolutionChoice) => {
  if (choice === "keep_target") return "Keep target";
  if (choice === "keep_source") return "Keep source";
  return "Skip source item";
};

function MergePlayersDialog({
  open,
  targetPlayer,
  onClose,
  onMerged,
}: MergePlayersDialogProps) {
  const [players, setPlayers] = useState<Player[]>([]);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [selectedSource, setSelectedSource] = useState<Player | null>(null);
  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState("");
  const [resolutions, setResolutions] = useState<Record<string, MergeResolutionChoice>>({});

  useEffect(() => {
    if (!open) {
      setPlayers([]);
      setSelectedSource(null);
      setPreview(null);
      setPreviewLoading(false);
      setMergeLoading(false);
      setConfirmDelete(false);
      setError("");
      setResolutions({});
      return;
    }

    let cancelled = false;

    const loadPlayers = async () => {
      setPlayersLoading(true);
      try {
        const response = await apiRequest("/api/players");
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || "Failed to fetch players");
        }

        if (!cancelled) {
          setPlayers(
            (Array.isArray(data) ? data : []).filter(
              (player: Player) => player.id !== targetPlayer?.id,
            ),
          );
        }
      } catch (loadError: any) {
        if (!cancelled) {
          setError(loadError?.message || "Failed to fetch players");
        }
      } finally {
        if (!cancelled) {
          setPlayersLoading(false);
        }
      }
    };

    loadPlayers();

    return () => {
      cancelled = true;
    };
  }, [open, targetPlayer?.id]);

  const handlePreview = async () => {
    if (!targetPlayer || !selectedSource) {
      setError("Select the duplicate player profile to merge into the target profile");
      return;
    }

    setPreviewLoading(true);
    setPreview(null);
    setError("");
    setConfirmDelete(false);

    try {
      const response = await apiRequest(`/api/players/${targetPlayer.id}/merge-preview`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ sourcePlayerId: selectedSource.id }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to preview merge");
      }

      setPreview(data);
      setResolutions({});
    } catch (previewError: any) {
      setError(previewError?.message || "Failed to preview merge");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleResolutionChange = (
    conflict: MergeConflict,
    action: MergeResolutionChoice,
  ) => {
    setResolutions((current) => ({
      ...current,
      [`${conflict.type}:${conflict.key}`]: action,
    }));
  };

  const handleMerge = async () => {
    if (!targetPlayer || !selectedSource || !preview) {
      return;
    }

    const missingResolution = preview.conflicts.some(
      (conflict) => !resolutions[`${conflict.type}:${conflict.key}`],
    );
    if (missingResolution) {
      setError("Every conflict needs an explicit resolution before merging");
      return;
    }

    if (!confirmDelete) {
      setError(`Confirm permanent deletion of ${selectedSource.name}'s profile before merging`);
      return;
    }

    setMergeLoading(true);
    setError("");

    try {
      const payload: { sourcePlayerId: string; resolutions: MergeResolution[] } = {
        sourcePlayerId: selectedSource.id,
        resolutions: Object.entries(resolutions).map(([compoundKey, action]) => {
          const separatorIndex = compoundKey.indexOf(":");
          return {
            type: compoundKey.slice(0, separatorIndex),
            key: compoundKey.slice(separatorIndex + 1),
            action,
          };
        }),
      };

      const response = await apiRequest(`/api/players/${targetPlayer.id}/merge`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to merge players");
      }

      onMerged();
      onClose();
    } catch (mergeError: any) {
      setError(mergeError?.message || "Failed to merge players");
    } finally {
      setMergeLoading(false);
    }
  };

  const unresolvedConflictCount = preview
    ? preview.conflicts.filter((conflict) => !resolutions[`${conflict.type}:${conflict.key}`]).length
    : 0;

  return (
    <Dialog open={open} onClose={mergeLoading ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Merge Player Profiles</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Keep {targetPlayer?.name} as the survivor profile and merge another profile into it.
        </Typography>

        <Autocomplete
          options={players}
          loading={playersLoading}
          value={selectedSource}
          onChange={(_, value) => {
            setSelectedSource(value);
            setPreview(null);
            setConfirmDelete(false);
            setResolutions({});
          }}
          getOptionLabel={(option) => `${option.name}${option.email ? ` • ${option.email}` : ""}`}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Duplicate profile to merge"
              margin="dense"
              helperText="Pick the profile that should be removed after the merge"
            />
          )}
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, mb: 2 }}>
          <Button
            variant="outlined"
            onClick={handlePreview}
            disabled={!selectedSource || previewLoading || playersLoading}
          >
            {previewLoading ? "Checking…" : "Analyze Merge"}
          </Button>
        </Box>

        {preview && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Additions to {preview.target.name}
              </Typography>
              <Typography variant="body2">
                {preview.additions.leagueDays.length} league day entries, {preview.additions.tournaments.length} tournament entries, {preview.additions.bets.length} bets.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Other references: {Object.entries(preview.additions.simpleCounts)
                  .filter(([, count]) => count > 0)
                  .map(([label, count]) => `${label} (${count})`)
                  .join(", ") || "none"}
              </Typography>
            </Box>

            {preview.conflicts.length > 0 ? (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Conflicts
                </Typography>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {unresolvedConflictCount > 0
                    ? `${unresolvedConflictCount} conflict${unresolvedConflictCount === 1 ? "" : "s"} still need a resolution.`
                    : "All conflicts have explicit resolutions."}
                </Alert>
                {preview.conflicts.map((conflict, index) => {
                  const resolutionKey = `${conflict.type}:${conflict.key}`;
                  const value = resolutions[resolutionKey] || "";
                  return (
                    <Box key={resolutionKey} sx={{ mb: 2 }}>
                      {index > 0 && <Divider sx={{ mb: 2 }} />}
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {conflict.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Source: {preview.source.name} • Target: {preview.target.name}
                      </Typography>
                      <RadioGroup
                        value={value}
                        onChange={(event) => handleResolutionChange(conflict, event.target.value as MergeResolutionChoice)}
                      >
                        {(["keep_target", "keep_source", "skip"] as MergeResolutionChoice[]).map((choice) => (
                          <FormControlLabel
                            key={choice}
                            value={choice}
                            control={<Radio size="small" />}
                            label={resolutionLabel(choice)}
                          />
                        ))}
                      </RadioGroup>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Alert severity="success">No conflicts found. The merge can proceed after confirmation.</Alert>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmDelete}
                  onChange={(event) => setConfirmDelete(event.target.checked)}
                />
              }
              label={`I understand this permanently deletes ${preview.source.name}'s profile after the merge succeeds.`}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mergeLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleMerge}
          disabled={!preview || mergeLoading || unresolvedConflictCount > 0 || !confirmDelete}
        >
          {mergeLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} color="inherit" />
              <span>Merging…</span>
            </Box>
          ) : (
            "Merge Players"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MergePlayersDialog;